import { useMemo, useState } from "react";
import LedgerNumber from "@/components/LedgerNumber";
import { formatINR } from "@/lib/brand";
import YearBreakdown from "./YearBreakdown";
import CalculatorActions from "./CalculatorActions";

export default function ReverseCagrCalculator() {
  const [initial, setInitial] = useState(500000);
  const [cagr, setCagr] = useState(12);
  const [years, setYears] = useState(10);

  const { fv, gains, principalPct, gainsPct, rows } = useMemo(() => {
    const P = Number(initial) || 0;
    const r = (Number(cagr) || 0) / 100;
    const yrs = Number(years) || 0;
    const fv = P * Math.pow(1 + r, yrs);
    const gains = fv - P;
    const principalPct = fv > 0 ? (P / fv) * 100 : 0;
    const gainsPct = 100 - principalPct;
    const rows = [];
    for (let k = 1; k <= yrs; k++) {
      const vk = P * Math.pow(1 + r, k);
      rows.push({
        year: k,
        primary: vk,
        segments: [
          { value: P, className: "bg-ink", legendClassName: "bg-ink", label: "Investment" },
          { value: Math.max(0, vk - P), className: "bg-brass", legendClassName: "bg-brass", label: "Gains" },
        ],
        columns: [
          { value: formatINR(vk) },
          { value: formatINR(vk - P), accent: true },
        ],
      });
    }
    return { fv, gains, principalPct, gainsPct, rows };
  }, [initial, cagr, years]);

  return (
    <div>
      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 space-y-6">
          <NumField label="Initial Investment (₹)" value={initial} onChange={setInitial} min={0} testId="rcagr-initial" />
          <NumField label="Expected CAGR (%)" value={cagr} onChange={setCagr} step={0.1} min={0} max={40} testId="rcagr-rate" />
          <SliderField label="Number of Years" value={years} onChange={setYears} min={1} max={40} testId="rcagr-years" />
        </div>

        <div className="lg:col-span-7 card-paper p-6 md:p-8 ledger-rule" data-testid="rcagr-result">
          <div className="eyebrow mb-2">Projected Value in {years} years</div>
          <div className="text-[38px] md:text-[48px] leading-none text-ink">
            <LedgerNumber value={fv} testId="rcagr-future-value" />
          </div>

          <div className="mt-8">
            <div className="flex items-center gap-2 eyebrow mb-2">
              <span>Investment vs. Gains</span>
            </div>
            <div className="w-full h-3 rounded-sm overflow-hidden border hairline flex">
              <div style={{ width: `${principalPct}%` }} className="bg-ink transition-all duration-500" />
              <div style={{ width: `${gainsPct}%` }} className="bg-brass transition-all duration-500" />
            </div>
            <div className="flex justify-between mt-2 text-[12px] text-ink-soft">
              <span><span className="inline-block w-2 h-2 bg-ink mr-1.5" /> Investment · {principalPct.toFixed(0)}%</span>
              <span><span className="inline-block w-2 h-2 bg-brass mr-1.5" /> Gains · {gainsPct.toFixed(0)}%</span>
            </div>
          </div>

          <div className="mt-8 divide-y hairline border-t hairline border-b">
            <Row label="Initial Investment" value={formatINR(initial)} />
            <Row label="Total Gains" value={formatINR(gains)} accent />
            <Row label="Projected Final Value" value={formatINR(fv)} />
          </div>
        </div>
      </div>

      <YearBreakdown
        rows={rows}
        primaryLabel="Projected value at end of each year"
        tableHeaders={["Year", "Projected Value", "Cumulative Gains"]}
        testIdPrefix="rcagr"
      />

      <CalculatorActions
        testIdPrefix="rcagr"
        withStrategyCta
        buildPayload={() => ({
          type: "rcagr",
          title: "Reverse CAGR Calculator",
          one_liner: `a Reverse CAGR projection — ${formatINR(initial)} growing at ${cagr}% CAGR for ${years} years`,
          inputs: [
            { label: "Initial Investment", value: formatINR(initial) },
            { label: "Expected CAGR", value: `${cagr}%` },
            { label: "Number of Years", value: `${years} years` },
          ],
          hero: { label: `Projected Value in ${years} years`, value: formatINR(fv) },
          summary: [
            { label: "Initial Investment", value: formatINR(initial) },
            { label: "Total Gains", value: formatINR(gains), accent: true },
          ],
          row_headers: ["Year", "Projected Value", "Cumulative Gains"],
          rows: rows.map((r) => [String(r.year), formatINR(r.primary), formatINR(r.primary - initial)]),
        })}
      />
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between py-3">
      <span className="eyebrow">{label}</span>
      <span className={`num text-[16px] ${accent ? "text-brass-deep" : "text-ink"}`}>{value}</span>
    </div>
  );
}
function NumField({ label, value, onChange, step = 1, min, max, testId }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      <input
        type="number" value={value} step={step} min={min} max={max}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="fld num" data-testid={testId}
      />
    </label>
  );
}
function SliderField({ label, value, onChange, min, max, testId }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-2">
        <span className="eyebrow">{label}</span>
        <span className="num text-ink text-[14px]">{value} yrs</span>
      </div>
      <input
        type="range" min={min} max={max} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#14A5A5]" data-testid={testId}
      />
      <div className="flex justify-between text-[11px] text-ink-soft/80 mt-1 num">
        <span>{min}</span><span>{max}</span>
      </div>
    </label>
  );
}
